import { Link, router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import InfoTip from '../../../components/InfoTip'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function congestionColor(idx) {
  if (idx >= 0.8) return '#F44336'
  if (idx >= 0.6) return '#FF9800'
  if (idx >= 0.4) return '#FFC107'
  if (idx > 0)    return '#4CAF50'
  return '#CFD8DC'
}

function congestionLabel(idx) {
  if (idx >= 0.8) return 'CRIT'
  if (idx >= 0.6) return 'HIGH'
  if (idx >= 0.4) return 'MOD'
  if (idx > 0)    return 'LOW'
  return 'OFF'
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ANCHOR_ICONS = {
  gate:      '🚪',
  lounge:    '🛋',
  security:  '🔒',
  baggage:   '🧳',
  restaurant:'🍽',
  corridor:  '↔',
  default:   '●',
}

// ─── Airport SVG heat map ──────────────────────────────────────────────────────
// Renders anchors as a grid of colored dots on a stylized terminal floor plan.
// No real lat/lng coordinates needed — positions arranged by terminal/floor.

function AirportHeatMap({ airport, onSelectAnchor, selectedAnchorId }) {
  const { iata_code, airport_name, anchors } = airport

  if (!anchors || anchors.length === 0) {
    return (
      <div className="bg-gray-50 rounded border border-gray-200 p-4 text-center text-xs text-gray-400">
        No anchors mapped for {iata_code}
      </div>
    )
  }

  // Group by terminal
  const byTerminal = {}
  anchors.forEach(a => {
    const t = a.terminal || 'Terminal'
    if (!byTerminal[t]) byTerminal[t] = []
    byTerminal[t].push(a)
  })

  const terminals = Object.entries(byTerminal)
  const W = 560, H_PER_TERM = 90, PAD = 16
  const H = terminals.length * (H_PER_TERM + 4) + PAD * 2
  const DOT_R = 14

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-gray-800">
          ✈ {iata_code}
          <span className="ml-1.5 text-xs text-gray-400 font-normal">{airport_name}</span>
        </p>
        <span className="text-xs text-gray-400">{anchors.length} anchors</span>
      </div>

      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full rounded border border-gray-100"
        style={{ background: '#F5F7FA', maxWidth: W }}
      >
        {/* Terminal rows */}
        {terminals.map(([termName, termAnchors], ti) => {
          const rowY   = PAD + ti * (H_PER_TERM + 4)
          const colW   = (W - PAD * 2) / Math.max(termAnchors.length, 1)
          const clampedColW = Math.min(colW, 80)
          const rowX   = PAD + ((W - PAD * 2) - clampedColW * termAnchors.length) / 2

          return (
            <g key={termName}>
              {/* Terminal label */}
              <rect
                x={PAD} y={rowY + 2}
                width={W - PAD * 2} height={H_PER_TERM - 4}
                rx={6} fill="#FFFFFF" stroke="#E0E0E0" strokeWidth={1}
              />
              <text
                x={PAD + 8} y={rowY + 16}
                fontSize={9} fontWeight="700" fill="#90A4AE"
              >
                {termName}
              </text>

              {/* Anchor dots */}
              {termAnchors.map((anchor, ai) => {
                const cx    = rowX + ai * clampedColW + clampedColW / 2
                const cy    = rowY + H_PER_TERM / 2 + 4
                const color = congestionColor(anchor.congestion_index)
                const isSelected = selectedAnchorId === anchor.id
                const pulse = anchor.congestion_index >= 0.7

                return (
                  <g
                    key={anchor.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSelectAnchor(isSelected ? null : anchor)}
                  >
                    {/* Pulse ring for high congestion */}
                    {pulse && (
                      <circle cx={cx} cy={cy} r={DOT_R + 6} fill={color + '30'} />
                    )}
                    {/* Selection ring */}
                    {isSelected && (
                      <circle cx={cx} cy={cy} r={DOT_R + 3} fill="none" stroke={color} strokeWidth={2} />
                    )}
                    {/* Main dot */}
                    <circle cx={cx} cy={cy} r={DOT_R} fill={color} stroke="#fff" strokeWidth={1.5} />
                    {/* Dispatch indicator */}
                    {anchor.dispatch_count > 0 && (
                      <circle cx={cx + DOT_R - 3} cy={cy - DOT_R + 3} r={5} fill="#9C27B0" stroke="#fff" strokeWidth={1} />
                    )}
                    {/* Anchor icon text */}
                    <text x={cx} y={cy + 4} textAnchor="middle" fontSize={10} fill="#fff">
                      {ANCHOR_ICONS[anchor.anchor_type] || '●'}
                    </text>
                    {/* Label below */}
                    <text x={cx} y={cy + DOT_R + 11} textAnchor="middle" fontSize={7} fill="#607D8B" fontWeight="600">
                      {anchor.gate_ref || anchor.label?.slice(0, 8) || '?'}
                    </text>
                    {/* Congestion label above */}
                    <text
                      x={cx} y={cy - DOT_R - 3}
                      textAnchor="middle" fontSize={7}
                      fontWeight="700"
                      fill={color}
                    >
                      {congestionLabel(anchor.congestion_index)}
                    </text>
                  </g>
                )
              })}
            </g>
          )
        })}

        {/* Legend */}
        {[
          { label: 'Critical', color: '#F44336' },
          { label: 'High',     color: '#FF9800' },
          { label: 'Moderate', color: '#FFC107' },
          { label: 'Low',      color: '#4CAF50' },
          { label: 'Offline',  color: '#CFD8DC' },
        ].map(({ label, color }, i) => (
          <g key={label}>
            <circle cx={PAD + i * 75 + 6} cy={H - 8} r={5} fill={color} />
            <text x={PAD + i * 75 + 14} y={H - 4} fontSize={7} fill="#90A4AE">{label}</text>
          </g>
        ))}
        <circle cx={W - 60} cy={H - 8} r={5} fill="#9C27B0" />
        <text x={W - 52} y={H - 4} fontSize={7} fill="#90A4AE">Active dispatch</text>
      </svg>
    </div>
  )
}

// ─── Anchor detail panel ──────────────────────────────────────────────────────

function AnchorDetailPanel({ anchor, onClose }) {
  if (!anchor) return null
  const color = congestionColor(anchor.congestion_index)

  return (
    <div
      className="bg-white border-2 rounded-lg p-4"
      style={{ borderColor: color }}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-bold text-gray-900">
            {ANCHOR_ICONS[anchor.anchor_type] || '●'} {anchor.label}
          </p>
          <p className="text-xs text-gray-500">
            {[anchor.terminal, anchor.floor_level, anchor.gate_ref].filter(Boolean).join(' · ')}
          </p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs">
        <div className="bg-gray-50 rounded p-2.5 text-center">
          <p className="text-lg font-bold tabular-nums" style={{ color }}>
            {Math.round((anchor.congestion_index || 0) * 100)}%
          </p>
          <p className="text-gray-500">Congestion</p>
        </div>
        <div className="bg-gray-50 rounded p-2.5 text-center">
          <p className="text-lg font-bold tabular-nums" style={{ color: anchor.dispatch_count > 0 ? '#9C27B0' : '#4CAF50' }}>
            {anchor.dispatch_count ?? 0}
          </p>
          <p className="text-gray-500">Active Dispatches</p>
        </div>
        <div className="bg-gray-50 rounded p-2.5 text-center">
          <p className="text-lg font-bold text-gray-700 capitalize">{anchor.anchor_type || '—'}</p>
          <p className="text-gray-500">Anchor Type</p>
        </div>
      </div>
      <div className="mt-3 flex gap-2 flex-wrap">
        <a
          href="/admin/cloudtraq_gds/channel_sensor"
          className="text-xs px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded hover:bg-blue-100"
        >
          channelSensor Tile
        </a>
        <a
          href="/admin/cloudtraq_gds/dispatch_board"
          className="text-xs px-3 py-1.5 bg-orange-50 border border-orange-200 text-orange-700 rounded hover:bg-orange-100"
        >
          Dispatch Board
        </a>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function NodeHeatMap({ airports, tenant_branding }) {
  const intervalRef   = useRef(null)
  const [selected, setSelected]   = useState(null)

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      router.reload({ only: ['airports'] })
    }, 60_000)
    return () => clearInterval(intervalRef.current)
  }, [])

  const totalAnchors   = airports.reduce((n, a) => n + (a.anchors?.length || 0), 0)
  const criticalCount  = airports.flatMap(a => a.anchors || []).filter(a => (a.congestion_index || 0) >= 0.7).length
  const dispatchActive = airports.flatMap(a => a.anchors || []).reduce((n, a) => n + (a.dispatch_count || 0), 0)

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Node Heat Map</h1>
            <p className="text-sm text-gray-500 mt-1">
              PhysicalSenseAnchor congestion overlay · {airports.length} airports · {totalAnchors} anchors
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => router.reload()} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100">
              Refresh
            </button>
            <Link href="/admin/cloudtraq_gds" className="text-sm text-blue-600 hover:underline">← Command Center</Link>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold tabular-nums text-gray-700">{totalAnchors}</p>
            <p className="text-xs text-gray-500">Total Anchors</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold tabular-nums" style={{ color: criticalCount > 0 ? '#F44336' : '#4CAF50' }}>
              {criticalCount}
            </p>
            <p className="text-xs text-gray-500">High Congestion</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold tabular-nums" style={{ color: dispatchActive > 0 ? '#9C27B0' : '#9E9E9E' }}>
              {dispatchActive}
            </p>
            <p className="text-xs text-gray-500">Active Dispatches</p>
          </div>
        </div>

        {/* Anchor detail */}
        {selected && (
          <div className="mb-4">
            <AnchorDetailPanel anchor={selected} onClose={() => setSelected(null)} />
          </div>
        )}

        {/* Airport maps */}
        {airports.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-400 text-sm">No airport anchors mapped yet.</p>
            <p className="text-gray-400 text-xs mt-1">Create PhysicalSenseAnchor records with airport_level associations to see the heat map.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {airports.map(airport => (
              <AirportHeatMap
                key={airport.iata_code}
                airport={airport}
                selectedAnchorId={selected?.id}
                onSelectAnchor={setSelected}
              />
            ))}
          </div>
        )}

        {/* Nav */}
        <div className="mt-6 flex flex-wrap gap-4 text-sm text-gray-500">
          <Link href="/admin/cloudtraq_gds" className="text-blue-600 hover:underline">← Command Center</Link>
          <Link href="/admin/cloudtraq_gds/channel_sensor" className="text-blue-600 hover:underline">channelSensor Ops</Link>
          <Link href="/admin/cloudtraq_gds/high_risk" className="text-blue-600 hover:underline">High Risk PAX</Link>
        </div>
      </div>
    </div>
  )
}
